import { Droplet, BarChart3, BadgeCheck, Baby } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ActivityItem } from './types';

// App activity data
const activities: ActivityItem[] = [
    { time: "7:30 AM", status: "Dry", bgClass: "bg-success/10", textClass: "text-success", hoverClass: "hover:bg-success/20" },
    { time: "9:45 AM", status: "Changed", bgClass: "bg-secondary/10", textClass: "text-secondary", hoverClass: "hover:bg-secondary/20" },
    { time: "12:15 PM", status: "Changed", bgClass: "bg-secondary/10", textClass: "text-secondary", hoverClass: "hover:bg-secondary/20" },
    { time: "2:30 PM", status: "Dry", bgClass: "bg-success/10", textClass: "text-success", hoverClass: "hover:bg-success/20" }
];

export const AppFeatures = () => {
    return (
        <section className="container mx-auto px-8 py-16 items-center">
            <div className="grid md:grid-cols-2 gap-16 items-center">
                <div className="order-2 md:order-1 px-6 py-4">
                    <Badge className="text-sm mb-6 bg-secondary/10 text-secondary hover:bg-secondary/20 font-medium px-4 py-2">
                        Smart Analytics
                    </Badge>
                    <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-800">
                        Powerful Insights in Your Pocket
                    </h2>
                    <p className="text-gray-600 text-lg mb-10">
                        Our intuitive mobile app transforms diaper data into valuable health insights, helping you understand your baby's patterns and needs.
                    </p>

                    <Tabs defaultValue="monitor" className="w-full">
                        <TabsList className="mb-8 p-1">
                            <TabsTrigger value="monitor" className="nav-baby py-3 px-6">
                                <Droplet className="h-4 w-4 mr-2" />
                                Real-time Monitoring
                            </TabsTrigger>
                            <TabsTrigger value="analytics" className="nav-baby py-3 px-6">
                                <BarChart3 className="h-4 w-4 mr-2" />
                                Health Analytics
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="monitor" className="space-y-6 p-2">
                            <p className="text-gray-700">
                                Get instant alerts when your baby needs a diaper change, with wetness level indicators and temperature readings.
                            </p>
                            <ul className="space-y-4">
                                {[
                                    "Instant wetness notifications",
                                    "Temperature monitoring",
                                    "Customizable alert thresholds",
                                    "Multi-caregiver sharing"
                                ].map((item, index) => (
                                    <li key={index} className="flex items-center gap-3">
                                        <BadgeCheck className="h-5 w-5 text-success shrink-0" />
                                        <span className="text-gray-700">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </TabsContent>

                        <TabsContent value="analytics" className="space-y-6 p-2">
                            <p className="text-gray-700">
                                Track patterns over time to understand your baby's health and establish optimal routines.
                            </p>
                            <ul className="space-y-4">
                                {[
                                    "Hydration tracking and trends",
                                    "Diaper change frequency analysis",
                                    "Sleep correlation insights",
                                    "Export data for pediatrician sharing"
                                ].map((item, index) => (
                                    <li key={index} className="flex items-center gap-3">
                                        <BadgeCheck className="h-5 w-5 text-success shrink-0" />
                                        <span className="text-gray-700">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </TabsContent>
                    </Tabs>

                    <div className="mt-10">
                        <Button className="btn-baby bg-pink-600 text-white px-8 py-3 text-base">
                            Download App
                        </Button>
                    </div>
                </div>

                <div className="order-1 md:order-2 relative max-w-xs mx-auto">
                    <div className="relative z-10 w-[280px] h-[560px] bg-white rounded-[40px] shadow-2xl border-8 border-gray-200 overflow-hidden">
                        <div className="absolute top-0 left-0 right-0 h-12 bg-secondary/10 flex items-center justify-center rounded-t-[32px]">
                            <div className="w-32 h-5 bg-gray-200/50 rounded-full"></div>
                        </div>
                        <div className="pt-12 px-4 h-full bg-gradient-to-b from-white to-secondary/5">
                            <div className="bg-white rounded-2xl shadow-sm p-4 mb-4">
                                <div className="flex justify-between items-center mb-3">
                                    <h4 className="font-bold text-sm text-gray-800">Baby Emma</h4>
                                    <Badge className="bg-success/10 text-success hover:bg-success/20">Dry</Badge>
                                </div>
                                <div className="w-full h-32 rounded-lg bg-primary/5 flex items-center justify-center mb-3">
                                    <Baby className="h-14 w-14 text-primary/30" />
                                </div>
                                <div className="flex justify-between text-xs text-gray-500">
                                    <span>Last change: 30m ago</span>
                                    <span>Battery: 92%</span>
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl shadow-sm p-4 mb-4">
                                <h4 className="font-bold text-sm text-gray-800 mb-4">Daily Activity</h4>
                                <div className="space-y-3">
                                    {activities.map((item, index) => (
                                        <div key={index} className="flex justify-between items-center text-xs">
                                            <span className="text-gray-500">{item.time}</span>
                                            <Badge className={`${item.bgClass} ${item.textClass} ${item.hoverClass} text-xs h-6`}>
                                                {item.status}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="absolute -bottom-6 -right-6 -z-10 w-[240px] h-[480px] bg-gradient-to-br from-primary/30 to-secondary/30 rounded-[40px] blur-xl"></div>
                </div>
            </div>
        </section>
    );
};